import type { KycStatus, TipoDocumento } from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * Phase 18 v1.4 — KYC-01. Profissional-side submission helper.
 *
 * The submit endpoint is multipart/form-data (3 files + tipoDocumento) so it
 * does NOT route through ApiClient.post() — that helper serializes JSON. We
 * build a FormData and use fetch directly, reusing the configured baseUrl +
 * access token from the parent client.
 *
 * Response shape:
 *   { id: string, status: KycStatus, submetidoEm: string }
 *
 * The list/detail/decidir admin endpoints live in admin.ts.
 */
export interface KycSubmeterResult {
  id: string;
  status: KycStatus;
  submetidoEm: string;
}

export interface KycSubmeterInput {
  tipoDocumento: TipoDocumento;
  fotoFrente: Blob | File;
  fotoVerso: Blob | File;
  fotoSelfie: Blob | File;
}

export class KycEndpoints {
  constructor(private readonly client: ApiClient) {}

  /**
   * POST /profissionais/me/kyc — multipart submission.
   *
   * The browser sets the multipart/form-data Content-Type with the correct
   * boundary automatically when the body is a FormData; we MUST NOT pass a
   * Content-Type header manually or the boundary parameter goes missing and
   * the server can't parse the parts.
   */
  async submeter(input: KycSubmeterInput): Promise<KycSubmeterResult> {
    const form = new FormData();
    form.append('tipoDocumento', input.tipoDocumento);
    form.append('fotoFrente', input.fotoFrente);
    form.append('fotoVerso', input.fotoVerso);
    form.append('fotoSelfie', input.fotoSelfie);

    const url = `${this.client.baseUrl}/profissionais/me/kyc`;
    const headers: Record<string, string> = { Accept: 'application/json' };
    // Token sourced from the parent ApiClient config. We deliberately don't
    // use ApiClient.post() because it sets Content-Type: application/json,
    // which breaks multipart parsing.
    const token = await this.getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: form,
      credentials: 'include',
    });
    const text = await res.text();
    const json = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      throw new Error(json?.message ?? `KYC submeter falhou: ${res.status}`);
    }
    return json as KycSubmeterResult;
  }

  /**
   * Read the access token via the same accessor the parent client uses.
   * Exposed as a private helper here because ApiClientConfig.getAccessToken
   * is held privately on the parent — we re-derive via a private cast that
   * tolerates whatever Promise wrapping the host config uses.
   */
  private async getAccessToken(): Promise<string | null> {
    // Cheap reflection: the parent client stores `config` on a private field
    // but exposes it implicitly through any method that authorizes. We just
    // call a tiny dummy request through `auth` to grab the token? Simpler:
    // expose the accessor via the client's already-public `baseUrl` pattern.
    // For Phase 18 we ask the user of the api-client to also wire token
    // refresh upstream — the FormData send here intentionally fires with the
    // current token; the caller handles 401 → refresh externally for KYC
    // (rare endpoint, 1× per user lifetime).
    const cfg = (this.client as unknown as {
      config: { getAccessToken: () => string | null | Promise<string | null> };
    }).config;
    return cfg.getAccessToken();
  }
}
